import { useCallback, useEffect, useState } from 'react';
import { FaWhatsapp } from 'react-icons/fa';
import { LuArrowLeft, LuCheck, LuInfo, LuLock, LuPlug, LuQrCode, LuX } from 'react-icons/lu';
import aviso from '../lib/aviso';
import { MetaService, type MetaConfig } from '../services/MetaService';
import { ehAdministradorDaEmpresa, temFuncionalidade } from '../config/permissions';
import { useCelular } from '../hooks/useCelular';
import { useConectarWhatsappMeta } from '../hooks/useConectarWhatsappMeta';
import ConectarMetaPanel from './conexoes/ConectarMetaPanel';
import ConectarQrPanel from './conexoes/ConectarQrPanel';
/* Os painéis de conectar vestem as classes `cx-` de Conexões. */
import '../pages/ConnectionsPage.css';

interface Props {
    /** `tela`: sem conversas, ocupa a largura toda. `area`: no lugar da conversa, com a lista ao lado. */
    modo: 'tela' | 'area';
    /** Uso do plano para o WhatsApp; `limite` null = sem limite. */
    uso: { usado: number; limite: number | null };
    /** Motivo de o plano não deixar conectar mais um número (null = pode). */
    bloqueio: string | null;
    /** O número foi ligado: o Chat consulta o status de novo. */
    onConectado: () => void;
    /** QR Code que já existe e não está ativo: o cartão lê o código nele, sem criar outro. */
    qrPendente?: { id: number; nome?: string } | null;
    /** Celular com conversas: a escolha abriu por cima da lista e precisa de volta. */
    onVoltar?: () => void;
}

/**
 * Chat sem WhatsApp conectado — desenho fechado em 03/10/2026
 * (docs/designsystem/propostas/chat-sem-conexao.html). Prefixo `.ch-sc-`.
 *
 * As duas formas de conectar lado a lado, cada cartão com as mesmas três
 * partes (o que precisa ter · o que muda na prática · o botão), e o botão
 * conecta aqui mesmo, com os painéis de Conexões. Quem não é administrador vê
 * os requisitos sem o botão; com o QR Code desligado em Sistema, o cartão dele
 * não aparece (regra de 30/09: funcionalidade desligada não se mostra).
 */
const ChatSemConexao = ({ modo, uso, bloqueio, qrPendente = null, onConectado, onVoltar }: Props) => {
    const admin = ehAdministradorDaEmpresa();
    const qrLigado = temFuncionalidade('whatsapp_evolution');
    const celular = useCelular();

    const [metaConfig, setMetaConfig] = useState<MetaConfig | null>(null);
    const [painel, setPainel] = useState<'meta' | 'qr' | null>(null);

    useEffect(() => {
        if (!admin) return;
        MetaService.getMetaConfig().then(r => { if (r.sucesso && r.dados) setMetaConfig(r.dados); });
    }, [admin]);

    const { pronto, conectando, conectar } = useConectarWhatsappMeta({
        appId: metaConfig?.appId,
        configurationId: metaConfig?.configurationId,
        aoConectar: () => { setPainel(null); onConectado(); },
    });

    const fecharQr = useCallback(() => setPainel(null), []);

    const titulo = !admin
        ? 'O WhatsApp da empresa ainda não está conectado'
        : modo === 'area' ? 'O WhatsApp ainda não está conectado' : 'Conecte o WhatsApp para começar a atender';
    const apoio = !admin
        ? 'Só um administrador da empresa pode conectar. Abaixo está o que ele vai precisar, se quiser adiantar.'
        : modo === 'area'
            ? `As conversas que já existem continuam na lista. Para receber e responder pelo WhatsApp, ${qrLigado ? 'escolha como conectar' : 'conecte abaixo'}.`
            : celular
                ? 'Escolha abaixo como conectar. Assim que o número estiver ligado, as conversas passam a chegar aqui.'
                : `Nenhum número da empresa está ligado ao CRM ainda.${qrLigado ? ' Escolha abaixo como conectar.' : ''} Assim que o número estiver ligado, as conversas passam a chegar aqui.`;

    /* Rodapé do cartão: o botão, ou o motivo de não haver botão. */
    const trava = (texto: string) => (
        <div className="ch-sc-pe"><span className="ch-sc-trava"><LuLock size={14} />{texto}</span></div>
    );

    const requisitosMeta = (
        <>
            <div className="ch-sc-rot">Você vai precisar de</div>
            <ol className="ch-sc-req">
                <li><span className="ch-sc-n">1</span><span>Um portfólio empresarial no Meta Business</span></li>
                <li><span className="ch-sc-n">2</span><span>O número no app WhatsApp Business <small>Você continua usando o app no celular, no mesmo número.</small></span></li>
                <li><span className="ch-sc-n">3</span><span>Uma forma de pagamento cadastrada na Meta</span></li>
            </ol>
        </>
    );
    const praticaMeta = (
        <>
            <div className="ch-sc-rot">Na prática</div>
            <ul className="ch-sc-pr">
                <li className="is-sim"><LuCheck size={14} />Disparos em massa com templates aprovados</li>
                <li className="is-sim"><LuCheck size={14} />Funciona com o celular desligado</li>
                <li className="is-obs"><LuInfo size={14} />A Meta cobra pelos templates enviados, direto na sua conta</li>
                <li className="is-obs"><LuInfo size={14} />Depois de 24h sem resposta do cliente, só sai template</li>
            </ul>
        </>
    );
    const cabecaMeta = (
        <div className="ch-sc-h">
            <span className="ch-sc-bx is-wa" aria-hidden="true"><FaWhatsapp size={18} /></span>
            <div>
                <div className="ch-sc-nome">
                    API oficial da Meta {qrLigado && <span className="ch-sc-selo">Recomendado</span>}
                </div>
                <div className="ch-sc-sub">Parceiro oficial GB Code</div>
            </div>
        </div>
    );
    /* O QR Code pendente conta no plano: com 1 número, é ele que trava a API oficial. */
    const bloqueioMeta = bloqueio && qrPendente
        ? 'O QR Code pendente ocupa a vaga do plano. Leia o código ou remova-o em Conexões.'
        : bloqueio;
    const peMeta = !admin ? trava('Peça a um administrador para conectar') : bloqueioMeta ? trava(bloqueioMeta) : (
        <div className="ch-sc-pe">
            <button type="button" className="ch-sc-bt is-prim" onClick={conectar} disabled={!pronto || conectando}
                title={!pronto ? 'Carregando a configuração da Meta…' : undefined}>
                <FaWhatsapp size={13} /> {conectando ? 'Conectando…' : 'Conectar API oficial'}
            </button>
            <button type="button" className="ch-sc-lk" onClick={() => setPainel('meta')}>Ver passo a passo</button>
        </div>
    );

    return (
        <div className={`ch-sc cx-screen ${modo === 'area' ? 'is-area' : ''}`}>
            <div className="ch-sc-in">
                {onVoltar && (
                    <button type="button" className="ch-sc-voltar" onClick={onVoltar}>
                        <LuArrowLeft size={14} /> Voltar às conversas
                    </button>
                )}
                <div className="ch-sc-cab">
                    <span className="ch-sc-ico" aria-hidden="true"><LuPlug size={20} /></span>
                    <h2 className="ch-sc-t">{titulo}</h2>
                    <p className="ch-sc-s">{apoio}</p>
                </div>

                {qrLigado ? (
                    <div className={`ch-sc-g ${admin ? '' : 'is-curto'}`}>
                        <section className="ch-sc-op">
                            {cabecaMeta}
                            {requisitosMeta}
                            {admin && <div>{praticaMeta}</div>}
                            {peMeta}
                        </section>
                        <section className="ch-sc-op">
                            <div className="ch-sc-h">
                                <span className="ch-sc-bx is-qr" aria-hidden="true"><LuQrCode size={18} /></span>
                                <div>
                                    <div className="ch-sc-nome">Via QR Code</div>
                                    <div className="ch-sc-sub">Lê o código no celular, como no WhatsApp Web</div>
                                </div>
                            </div>
                            <div className="ch-sc-rot">Você vai precisar de</div>
                            <ol className="ch-sc-req">
                                <li><span className="ch-sc-n">1</span><span>O celular do número, com WhatsApp ou WhatsApp Business</span></li>
                                <li><span className="ch-sc-n">2</span><span>Ler o código em <i>Dispositivos conectados</i></span></li>
                                <li><span className="ch-sc-n">3</span><span>O celular ligado e com internet o tempo todo</span></li>
                            </ol>
                            {admin && (
                                <div>
                                    <div className="ch-sc-rot">Na prática</div>
                                    <ul className="ch-sc-pr">
                                        <li className="is-sim"><LuCheck size={14} />Conecta em um minuto, sem conta na Meta</li>
                                        <li className="is-sim"><LuCheck size={14} />Sem cobrança da Meta por mensagem</li>
                                        <li className="is-nao"><LuX size={14} />Não faz disparos em massa nem envia templates</li>
                                    </ul>
                                    {/* Quem está no próprio celular do número não aponta a câmera para a tela dele. */}
                                    {celular && (
                                        <p className="ch-sc-nota"><LuInfo size={12} />O código precisa ser lido pelo celular do número. Abra esta tela no computador para conectar.</p>
                                    )}
                                </div>
                            )}
                            {/* Reaproveitar o pendente não ocupa vaga nova do plano. */}
                            {!admin ? trava('Peça a um administrador para conectar') : bloqueio && !qrPendente ? trava(bloqueio) : (
                                <div className="ch-sc-pe">
                                    <button type="button" className="ch-sc-bt" onClick={() => setPainel('qr')}>
                                        <LuQrCode size={14} /> Conectar via QR Code
                                    </button>
                                </div>
                            )}
                        </section>
                    </div>
                ) : (
                    /* Uma forma só: o cartão alarga e os dois blocos ficam lado a lado. */
                    <section className="ch-sc-op is-unico">
                        {cabecaMeta}
                        <div className="ch-sc-duas">
                            <div>{requisitosMeta}</div>
                            {admin && <div>{praticaMeta}</div>}
                        </div>
                        {peMeta}
                    </section>
                )}
            </div>

            {admin && (
                <>
                    <ConectarMetaPanel
                        open={painel === 'meta'}
                        onClose={() => setPainel(null)}
                        onConectar={conectar}
                        conectando={conectando}
                        pronto={pronto}
                        uso={uso}
                    />
                    <ConectarQrPanel
                        open={painel === 'qr'}
                        onClose={fecharQr}
                        conexaoId={qrPendente?.id ?? null}
                        nome={qrPendente?.nome}
                        onConectado={() => { aviso.ok('WhatsApp conectado.'); onConectado(); }}
                    />
                </>
            )}
        </div>
    );
};

export default ChatSemConexao;
